import { useState } from "react";
import {
    Modal,
    Box,
    Typography,
    Divider,
    Button,
    Stack,
    Tooltip,
    IconButton,
    TextField,
    Autocomplete
} from "@mui/material";
import { QrCode2, Email, ContentCopy } from "@mui/icons-material";
import QRCode from "react-qr-code";
import Chip from "@mui/material/Chip";
import styles from "./../css/Quotes.module.css";
import QuoteService from "../../services/QuoteService";

export default function ShareQuoteModal({ open, onClose, quote, contacts, onEmailSent }) {
    const [shareMethod, setShareMethod] = useState(null);
    const [quoteUrl, setQuoteUrl] = useState("");
    const [toRecipients, setToRecipients] = useState([]);
    const [emailText, setEmailText] = useState("");

    const buildQuoteUrl = async () => {
        try {
            const data = await QuoteService.shareQuote(quote.id);
            setQuoteUrl(data.url);
        } catch (error) {
            console.error("Error fetching quote share URL:", error);
        }
    };

    const selectQrCode = () => {
        buildQuoteUrl();
        setShareMethod("QR");
    };

    const selectEmail = () => {
        setShareMethod("EMAIL");
        if (!emailText) {
            setEmailText(
                `Hallo ${quote?.customerName || "Kunde"},\n\n` +
                `anbei finden Sie das Angebot ${quote?.displayName || ""} als PDF-Anhang.\n` +
                `Bei Fragen stehen wir Ihnen gerne zur Verfügung.\n\n` +
                `Vielen Dank und freundliche Grüsse\n` +
                `Ihr Team`
            );
        }
    };

    const handleSendEmail = async () => {
        const requestData = { to: toRecipients, body: emailText };
        try {
            await QuoteService.sendQuoteEmail(quote.id, requestData);
            if (onEmailSent) onEmailSent();
        } catch (e) {
            console.error("Error sending quote email", e);
        }
        clearModal();
    };

    const clearModal = () => {
        onClose();
        setShareMethod(null);
        setQuoteUrl("");
        setToRecipients([]);
        setEmailText("");
    };

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;

    function normalizeToEmail(value) {
        if (!value) return "";
        if (typeof value === "string") {
            const m = value.match(/<([^>]+)>/);
            const maybe = m ? m[1] : value;
            return maybe.trim();
        }
        return value.email || value.name || "";
    }

    function uniqueEmails(arr) {
        const seen = new Set();
        return arr.filter((e) => {
            const k = e.toLowerCase();
            if (seen.has(k)) return false;
            seen.add(k);
            return true;
        });
    }

    return (
        <Modal open={open} onClose={clearModal}>
            <Box className={styles.sheet}>
                <Typography variant="h6">Angebot teilen</Typography>
                <Divider sx={{ my: 2 }} />

                <Stack direction="row" spacing={2} justifyContent="center" sx={{ mb: 2 }}>
                    <Button
                        variant={shareMethod === "QR" ? "contained" : "outlined"}
                        startIcon={<QrCode2 />}
                        onClick={selectQrCode}
                        sx={{ bgcolor: shareMethod === "QR" ? "#427898" : undefined }}
                    >
                        QR Code
                    </Button>
                    <Button
                        variant={shareMethod === "EMAIL" ? "contained" : "outlined"}
                        startIcon={<Email />}
                        onClick={selectEmail}
                        sx={{ bgcolor: shareMethod === "EMAIL" ? "#427898" : undefined }}
                    >
                        E-Mail senden
                    </Button>
                </Stack>

                {shareMethod === "QR" && (
                    <Stack alignItems="center" spacing={1.5}>
                        {quoteUrl ? (
                            <QRCode value={quoteUrl} size={200} />
                        ) : (
                            <Box sx={{ width: 200, height: 200, display: "grid", placeItems: "center" }}>
                                <Typography variant="body2" color="text.secondary">Generiere Link…</Typography>
                            </Box>
                        )}
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="body2" sx={{ wordBreak: "break-all" }}>{quoteUrl}</Typography>
                            <Tooltip title="Link kopieren">
                                <IconButton size="small" onClick={async () => {
                                    try { await navigator.clipboard.writeText(quoteUrl); } catch {}
                                }}>
                                    <ContentCopy fontSize="inherit" />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </Stack>
                )}

                {shareMethod === "EMAIL" && (
                    <Stack spacing={1.5}>
                        <Autocomplete
                            multiple
                            freeSolo
                            options={(contacts || []).map(c => ({
                                label: c.email ? `${c.firstName || ""} ${c.lastName || ""} <${c.email}>`.trim() : "",
                                email: c.email,
                            }))}
                            value={toRecipients}
                            onChange={(_, newValue) => {
                                const emails = uniqueEmails(
                                    newValue.map(normalizeToEmail).filter(Boolean)
                                );
                                setToRecipients(emails);
                            }}
                            getOptionLabel={(opt) => (typeof opt === "string" ? opt : opt.label || opt.email || "")}
                            renderTags={(value, getTagProps) =>
                                value.map((email, index) => (
                                    <Chip
                                        {...getTagProps({ index })}
                                        key={email}
                                        label={email}
                                        color={emailRegex.test(email) ? "default" : "error"}
                                        variant="outlined"
                                        size="small"
                                    />
                                ))
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="An"
                                    placeholder="E-Mail eingeben und Enter drücken…"
                                    error={toRecipients.some((e) => !emailRegex.test(e))}
                                    helperText={
                                        toRecipients.some((e) => !emailRegex.test(e))
                                            ? "Eine oder mehrere Adressen sind ungültig."
                                            : ""
                                    }
                                />
                            )}
                            selectOnFocus
                            clearOnBlur
                            handleHomeEndKeys
                            filterSelectedOptions
                            fullWidth
                        />

                        <TextField
                            label="Nachricht"
                            multiline
                            minRows={6}
                            value={emailText}
                            onChange={(e) => setEmailText(e.target.value)}
                            fullWidth
                        />

                        <Stack direction="row" justifyContent="flex-end" spacing={1}>
                            <Button onClick={clearModal}>Abbrechen</Button>
                            <Button
                                variant="contained"
                                sx={{ bgcolor: "#427898" }}
                                onClick={handleSendEmail}
                                disabled={toRecipients.length === 0 || toRecipients.some((e) => !emailRegex.test(e))}
                            >
                                Senden
                            </Button>
                        </Stack>
                    </Stack>
                )}

                {!shareMethod && (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center" }}>
                        Wählen Sie eine Methode oben aus, um fortzufahren.
                    </Typography>
                )}
            </Box>
        </Modal>
    );
}
